import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Star, MessageCircle, Trophy, Clock } from "lucide-react";

const stats = [
    {
        value: 450,
        suffix: "+",
        decimals: 0,
        label: "Jobs completed",
        unit: "neat finishes",
        icon: Trophy,
    },
    {
        value: 5.0,
        suffix: "",
        decimals: 1,
        label: "Google rating",
        unit: "out of 5.0",
        icon: Star,
    },
    {
        value: 12,
        suffix: "",
        decimals: 0,
        label: "Five-star reviews",
        unit: "verified Google",
        icon: MessageCircle,
    },
    {
        value: 5,
        suffix: "h",
        decimals: 0,
        label: "Replies usually under",
        unit: "weekdays",
        icon: Clock,
    },
    {
        value: null,
        text: "Insured",
        label: "Public liability insured",
        unit: "outdoor work",
        icon: ShieldCheck,
    },
];

function Counter({ target, suffix, decimals = 0 }) {
    const [val, setVal] = useState(0);
    const ref = useRef(null);
    const started = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting && !started.current) {
                        started.current = true;
                        const duration = 1500;
                        const start = performance.now();
                        const run = (t) => {
                            const p = Math.min((t - start) / duration, 1);
                            const eased = 1 - Math.pow(1 - p, 3);
                            setVal(target * eased);
                            if (p < 1) requestAnimationFrame(run);
                        };
                        requestAnimationFrame(run);
                    }
                });
            },
            { threshold: 0.4 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [target]);

    const display =
        decimals === 0
            ? Math.round(val).toLocaleString()
            : val.toFixed(decimals);
    return (
        <span ref={ref}>
            {display}
            {suffix}
        </span>
    );
}

export default function TrustStats() {
    return (
        <section
            id="trust"
            data-testid="section-trust"
            className="relative bg-[var(--ink)] text-[var(--paper)] py-28 md:py-36 grain border-t border-line-dark"
        >
            <div className="maxw section-pad relative z-10">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                    <div className="md:col-span-7">
                        <div className="eyebrow-light mb-5">Verified track record</div>
                        <h2 className="font-display font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                            Real numbers from a{" "}
                            <span className="italic text-[var(--terra-soft)]">
                                local crew
                            </span>
                            .
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:text-right">
                        <div className="font-mono-x text-[12.5px] text-[rgba(242,237,227,0.66)]">
                            Verified · Google reviews + insurance on file
                        </div>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[var(--line-dark)] border border-line-dark rounded-2xl overflow-hidden">
                    {stats.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={i}
                                className="bg-[var(--ink)] p-7 md:p-8 flex flex-col gap-4 min-h-[200px]"
                                data-testid={`trust-stat-${i}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="w-10 h-10 rounded-xl bg-[rgba(92,125,58,0.18)] text-[var(--terra-soft)] flex items-center justify-center">
                                        <Icon size={16} strokeWidth={1.7} />
                                    </span>
                                    <span className="font-mono-x text-[10.5px] text-[rgba(242,237,227,0.5)]">
                                        {String(i + 1).padStart(2, "0")} / 05
                                    </span>
                                </div>
                                <div className="stat-num text-5xl md:text-6xl">
                                    {s.value !== null ? (
                                        <Counter
                                            target={s.value}
                                            suffix={s.suffix}
                                            decimals={s.decimals}
                                        />
                                    ) : (
                                        <span>{s.text}</span>
                                    )}
                                </div>
                                <div className="hr-line-dark my-1" />
                                <div className="flex items-end justify-between gap-3">
                                    <div className="text-[13px] text-[rgba(242,237,227,0.78)] max-w-[200px] leading-snug">
                                        {s.label}
                                    </div>
                                    <div className="font-mono-x text-[10.5px] text-[rgba(242,237,227,0.45)]">
                                        {s.unit}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <p className="mt-6 font-mono-x text-[11px] text-[rgba(242,237,227,0.55)]">
                    Stats verified from completed jobs &amp; public Google reviews. Insurance cover for outdoor property maintenance work.
                </p>
            </div>
        </section>
    );
}
